import CSS from "csstype";
import "./EndPanel.css";
import { Player, PlayerEndResults, ResultCard, TurnInfo, CtaCard } from "../../shared/interfaces/GameLogicTypes";
import { track } from "../../shared/analytics";
import { useEffect, useRef, useState } from "react";
import CommunityApi from "../../shared/api/CommunityApi";
import { t } from "i18next";
import { Trans } from "react-i18next";

import { GiEntryDoor } from "react-icons/gi";
import { BiMailSend } from "react-icons/bi";
import { useHistory } from "react-router";
import Dialog from "../Dialog/Dialog";
import Feedback from "../../shared/api/FeedbackApi";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Icon,
  Grid,
  Divider,
} from "@mui/material";
import { Stack } from "@mui/system";
import useSocketContext from "../../shared/useSocketContext";
import ResultCardGrid from "../ResultCardGrid/ResultCardGrid";
import GameReview from "../GameReview/GameReview";
import RateReviewIcon from "@mui/icons-material/RateReview";
import StyleIcon from "@mui/icons-material/Style";
import PeopleIcon from "@mui/icons-material/People";
import StarIcon from "@mui/icons-material/Star";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CampaignIcon from "@mui/icons-material/Campaign";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShareIcon from "@mui/icons-material/Share";

function TikTokIcon({ fontSize = 36 }: { fontSize?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={fontSize} height={fontSize} fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5
               2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01
               a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34
               6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
    </svg>
  );
}

export interface EndPanelProps {
  playerName: string;
  playerEndResults: undefined | PlayerEndResults;
  isHost: boolean;
  resultCards: ResultCard[];
  turnHistory: TurnInfo[];
  players?: { [playerId: string]: Player };
  waitForHost: boolean;
  isReady: boolean;
  readyPlayerIds: string[];
  totalPlayers: number;
  onReadyToggle: (ready: boolean) => void;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  style:          <StyleIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  people:         <PeopleIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  tiktok:         <Box sx={{ color: "#ec7777" }}><TikTokIcon fontSize={36} /></Box>,
  star:           <StarIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  emoji_events:   <EmojiEventsIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  favorite:       <FavoriteIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  campaign:       <CampaignIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  open_in_new:    <OpenInNewIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  sports_esports: <SportsEsportsIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
  share:          <ShareIcon sx={{ fontSize: 36, color: "#ec7777" }} />,
};

export default function EndPanel(props: EndPanelProps) {
  const [feedBackDialog, setFeedBackDialog] = useState(false);
  const history = useHistory();
  const messageInputEl = useRef<HTMLTextAreaElement>(null);
  const [sent, setSent] = useState(false);
  const [valid, setValid] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [ctaCards, setCtaCards] = useState<CtaCard[]>([]);
  const socket = useSocketContext().socket;

  useEffect(() => {
    CommunityApi.getCtaCards().then(setCtaCards);
  }, []);


  useEffect(() => {
    if ((messageInputEl.current?.value as unknown as string) !== "") {
      setValid(true);
    }
  }, [messageInputEl.current?.value as unknown as string]);

  const hasCards = props.resultCards && props.resultCards.length > 0;
  const hasTurnHistory = props.turnHistory && props.turnHistory.length > 0;
  const players = props.players ?? {};

  return (
    <Box
      id="box1"
      sx={{
        position: { xs: "relative", md: "auto" },
        minHeight: "100vh",
        overflow: "auto",
        paddingBottom: "2em",
      }}
    >
      <Stack justifyContent="flex-start" alignItems={"center"} spacing={3} sx={{ paddingTop: "2em", paddingInline: "1em" }}>

        {/* Header */}
        <Stack alignItems={"center"}>
          <Typography variant="h1" sx={{ fontSize: 26 }}>
            {t("thanks_for_playing")}
          </Typography>
          <Typography variant="h1" sx={{ fontSize: 26 }}>
            {t("more_stuff")}
          </Typography>
        </Stack>

        {/* Result cards section */}
        {hasCards ? (
          <ResultCardGrid
            cards={props.resultCards}
            playerNames={Object.values(players).map((p) => p.name)}
          />
        ) : (
          /* Fallback: static per-player stats */
          props.playerEndResults != undefined && (
            <Stack
              justifyContent={"center"}
              direction={"column"}
              sx={{ width: "90%" }}
              spacing={2}
              alignItems="center"
            >
              <Paper sx={{ width: { md: "40%", xs: "70vw" }, maxHeight: "20vh" }}>
                <Stack sx={{ padding: ".5em" }}>
                  <Typography variant="h6" sx={{ fontSize: 22 }}>
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ winnerName: props.playerEndResults["winnerName"] }}
                    >
                      {"winner"}
                    </Trans>
                  </Typography>
                  <Typography variant="subtitle1">
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ winnerScore: props.playerEndResults["winnerScore"] }}
                    >
                      {"winning_score"}
                    </Trans>
                  </Typography>
                </Stack>
              </Paper>
              <Paper sx={{ width: { md: "40%", xs: "70vw" }, maxHeight: "20vh" }}>
                <Stack sx={{ padding: ".5em" }}>
                  <Typography variant="h6" sx={{ fontSize: 22 }}>
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ bestPlayerName: props.playerEndResults["bestPlayerName"] }}
                    >
                      {"knows_you_best"}
                    </Trans>
                  </Typography>
                  <Typography variant="subtitle1">
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ bestPlayerAccuracy: props.playerEndResults["bestPlayerAccuracy"] }}
                    >
                      {"best_accuracy"}
                    </Trans>
                  </Typography>
                </Stack>
              </Paper>
              <Paper sx={{ width: { md: "40%", xs: "70vw" }, maxHeight: "20vh" }}>
                <Stack sx={{ padding: ".5em" }}>
                  <Typography variant="h6" sx={{ fontSize: 22 }}>
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ worstPlayerName: props.playerEndResults["worstPlayerName"] }}
                    >
                      {"knows_you_worst"}
                    </Trans>
                  </Typography>
                  <Typography variant="subtitle1">
                    <Trans
                      components={{ h: <span className="highlightedText" /> }}
                      values={{ worstPlayerAccuracy: props.playerEndResults["worstPlayerAccuracy"] }}
                    >
                      {"worst_accuracy"}
                    </Trans>
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          )
        )}

        {/* CTA cards */}
        {ctaCards.length > 0 && (
          <Box sx={{ width: "100%", maxWidth: "900px" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {ctaCards.map((card) => (
                <a
                  key={card.id}
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ flex: 1, textDecoration: "none", display: "flex" }}
                  onClick={() => track("cta_click", { cardId: card.id, cardTitle: card.title, link: card.link })}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2.5, borderRadius: 3, display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 1, cursor: "pointer", flex: 1,
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": { transform: "translateY(-3px)", boxShadow: 5 },
                    }}
                  >
                    {ICON_MAP[card.icon] ?? <StyleIcon sx={{ fontSize: 36, color: "#ec7777" }} />}
                    <Typography variant="h6" fontWeight="bold" textAlign="center">{card.title}</Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">{card.body}</Typography>
                  </Paper>
                </a>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ width: "100%", maxWidth: "900px" }} />

        {/* Review Game button */}
        {hasTurnHistory && (
          <Button
            variant="outlined"
            startIcon={<RateReviewIcon />}
            onClick={() => setReviewOpen(true)}
          >
            {t("end_panel_review_game")}
          </Button>
        )}

        {/* Feedback */}
        <Paper
          sx={{
            width: { xs: "90%", md: "50vw" },
            padding: ".5em",
          }}
        >
          {!sent ? (
            <>
              <Stack
                alignItems={"center"}
                spacing={1}
                sx={{ width: "100%" }}
              >
                <Typography variant="h6" sx={{ fontSize: 18 }}>
                  <Trans components={{ h: <span className="highlightedText" /> }}>
                    {t("quickFeedBackQuestion")}
                  </Trans>
                </Typography>
                <Stack
                  direction="row"
                  alignItems={"center"}
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                >
                  <TextField
                    onChange={() => {
                      setValid((messageInputEl.current?.value as unknown as string) !== "");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && valid) {
                        e.preventDefault();
                        Feedback.submitFeedback(
                          messageInputEl.current?.value as unknown as string,
                          props.playerName,
                          sessionStorage.getItem("lobbyKey") || undefined
                        );
                        setSent(true);
                      }
                    }}
                    placeholder={t("quick_feedback")}
                    className="quickFeedbackText"
                    inputProps={{ ref: messageInputEl, maxLength: 2000 }}
                    sx={{ height: "100%", width: "100%" }}
                    multiline
                    rows={4}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          disabled={!valid}
                          onClick={() => {
                            Feedback.submitFeedback(
                              messageInputEl.current?.value as unknown as string,
                              props.playerName,
                              sessionStorage.getItem("lobbyKey") || undefined
                            );
                            setSent(true);
                          }}
                        >
                          <BiMailSend fontSize={25}></BiMailSend>
                        </IconButton>
                      ),
                    }}
                  />
                </Stack>
              </Stack>
            </>
          ) : (
            <Stack alignItems={"center"} justifyContent="center" sx={{ height: "100%" }}>
              <Typography variant="h6" sx={{ fontSize: 18 }}>
                {t("thx4Impro")}
              </Typography>
            </Stack>
          )}
        </Paper>

        {/* Restart / Leave buttons */}
        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ width: { xs: "90%", md: "50vw" } }}>
          <Button
            variant="contained"
            color="inherit"
            onClick={() => {
              sessionStorage.removeItem("playerId");
              sessionStorage.removeItem("lobbyKey");
              history.push("/play");
            }}
          >
            <GiEntryDoor fontSize={25} />
          </Button>
          {props.waitForHost ? (
            props.isHost ? (
              <Button variant="contained" onClick={() => socket.emit("restartLobby")}>
                {t("restart_lobby")}
              </Button>
            ) : (
              <Typography variant="body1" color="text.secondary">
                {t("wait_for_restart")}
              </Typography>
            )
          ) : (
            <>
              <Button
                variant={props.isReady ? "outlined" : "contained"}
                onClick={() => props.onReadyToggle(!props.isReady)}
              >
                {props.isReady ? t("unready") : t("end_panel_play_again")} ({props.readyPlayerIds.length}/{props.totalPlayers})
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Game Review Dialog */}
      <GameReview
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        turnHistory={props.turnHistory}
        players={players}
      />
    </Box>
  );
}
